import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/cn';

/**
 * shared/ui/text-link — Inline text link styled to the brand primary color.
 *
 * Renders an <a> by default. Pass `asChild` to compose it onto another
 * element (e.g. a router <Link> or a <button> acting as a link) via
 * Radix Slot — mirrors the `asChild` pattern used by Button.
 */
const textLinkVariants = cva(
  'inline-flex items-center gap-1.5 rounded-sm font-medium text-primary underline underline-offset-4 decoration-primary/40 transition-colors hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
);

export interface TextLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof textLinkVariants> {
  asChild?: boolean;
}

const TextLink = React.forwardRef<HTMLAnchorElement, TextLinkProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a';
    return (
      <Comp
        className={cn(textLinkVariants({ className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
TextLink.displayName = 'TextLink';

export { TextLink, textLinkVariants };
